import { useEffect, useRef } from 'react';
import { Button, Box as GrommetBox, Spinner } from 'grommet';
import { Checkmark, Close } from 'grommet-icons';
import { Box } from '/src/components';
import { Card, CardBody, CardHeader, Text } from '/src/components/common';
import { CreditCalculationStatus } from '/src/hooks/useCreditCalculation';

export const CreditCalculationLogPanel: React.FC<{
  status: CreditCalculationStatus;
  logLines: string[];
  updatedCount: number | null;
  failureMessage: string | null;
  onCancel: () => void;
}> = ({ status, logLines, updatedCount, failureMessage, onCancel }) => {
  const logContainerRef = useRef<HTMLDivElement>(null);
  const isPinnedToBottomRef = useRef(true);

  const handleLogScroll = () => {
    const logContainer = logContainerRef.current;
    if (logContainer) {
      const distanceFromBottom = logContainer.scrollHeight - logContainer.scrollTop - logContainer.clientHeight;
      isPinnedToBottomRef.current = distanceFromBottom < 40;
    }
  };

  useEffect(() => {
    const logContainer = logContainerRef.current;
    if (logContainer && isPinnedToBottomRef.current) {
      logContainer.scrollTop = logContainer.scrollHeight;
    }
  }, [logLines.length]);

  if (status === 'idle' || status === 'submitting') return null;

  const statusBadge = (() => {
    switch (status) {
      case 'done':
        return {
          text: `Done — ${updatedCount} equipment usages updated`,
          color: 'status-ok',
          icon: <Checkmark size="small" color="status-ok" />,
        };
      case 'failed':
        return {
          text: failureMessage,
          color: 'status-critical',
          icon: <Close size="small" color="status-critical" />,
        };
      case 'cancelled':
        return {
          text: 'Cancelled — no changes were saved',
          color: 'dark-2',
          icon: <Close size="small" color="dark-2" />,
        };
      default:
        return null;
    }
  })();

  return (
    <Card margin={{ vertical: 'medium' }} background="white" elevation="none" round="xsmall">
      <CardHeader
        pad={{ horizontal: 'medium', vertical: 'medium' }}
        border={{ side: 'bottom', color: 'light-4', size: '1px' }}
      >
        <Box direction="row" align="center" gap="small" fill="horizontal">
          <Box direction="row" align="center" gap="small">
            <Text size="large" weight="normal">
              Calculation Log
            </Text>
            {status === 'running' && (
              <Box direction="row" align="center" gap="xsmall">
                <Spinner size="xsmall" />
                <Text size="small" color="dark-2">
                  Running...
                </Text>
              </Box>
            )}
            {statusBadge !== null && (
              <Box direction="row" align="center" gap="xsmall">
                <Text size="small" color={statusBadge.color}>
                  {statusBadge.text}
                </Text>
                {statusBadge.icon}
              </Box>
            )}
          </Box>
          {status === 'running' && (
            <Button
              size="small"
              label="Cancel"
              style={{ fontWeight: 'normal', borderRadius: '6px' }}
              onClick={onCancel}
            />
          )}
        </Box>
      </CardHeader>
      <CardBody pad="medium">
        <GrommetBox ref={logContainerRef} onScroll={handleLogScroll} height={{ max: '400px' }} overflow="auto">
          <Text size="small" fontFamily="monospace" style={{ whiteSpace: 'pre-wrap' }}>
            {logLines.join('\n')}
          </Text>
        </GrommetBox>
      </CardBody>
    </Card>
  );
};
